import { logger } from '@/util/logger';

const $ = window.$;

export default function(id, settings) {
	/***
        SETTINGS:

        -settings.columns [required] array
        Each array item (column)is a javascript object with the following properties:
        "header": [required] string, the column header. This must match the cell class name for the column (e.g. rt-cell--header)
        "grow": [optional] integer, maps directly to the flex-grow CSS property. Indicates how much available space to allocate to a non-fixed column. If no "grow" specified, column will stay fixed at its min width
        "minWidth": [optional] integer, px value indicating the minimum amount of available pixels at which this column should be displayed.
        "maxWidth": [optional] integer, px value indicating the maximum amount of available pixels at which to display this column.
        "align": [optional] string, maps to text-align CSS property. Defines how data should be aligned within column cells
        "overflow": [optional] string, maps to text-overflow CSS property. Defines how text overflow should be handled within cells
        "hideOrder": [optional] integer, number indicating the order in which hideable columns should be hidden. 1 = first column to be hidden.

        Example columns array:
        -----------------------------------
        var columns = [{
            'header': 'action',
            'minWidth': 70,
            'overflow': 'clip',
            'align': 'center'
        },{
            'header': 'fName',
            'minWidth': 90,
            'maxWidth': 130,
            'grow':1
        }, {
            'header': 'lName',
            'minWidth': 90,
            'maxWidth': 130,
            'grow':1
        }, {
            'header': 'title',
            'minWidth': 140,
            'hideOrder':3
        }, {
            'header': 'company',
            'minWidth': 100,
            'grow': 2,
            'hideOrder':1
        }, {
            'header': 'relationship',
            'minWidth': 120,
            'grow':1,
            'hideOrder': 2,
        }, {
            'header': 'group',
            'minWidth': 100,
            'maxWidth': 210,
            'grow':2
        }];

        -settings.shortDateWidth [optional] integer - specifies the window width value in pixels at which the date should be converted from mm/dd/yyyy to mm/dd/yy format. Leave blank
        -settings.members [optional] string - **NOTE: NOT USUALLY REQUIRED** comma separated list of rendered datasets that belong to this responsive table. See Wallet.aspx in unmerged view for example
        -settings.isSortable [optional] - Indicates whether the dataset should be made sortable by column header. False by default
        -settings.sortMethod [optional] - if sortable, sortMethod is the column header to be sorted on initialization
        -settings.sortOrder [optional] = if sortable, sortOrder is the order (reverse or standard) in which to sort on initialization
        -settings.sortType [optional] = if sortable, sortType is the type of sort to perform upon initialization (e.g. alphabetical vs numeric)

    ***/
	this.s = settings || {};
	this.id = id;

	this.$outer = $('#' + this.id);
	this.$filters;

	// create style nodes
	this.baseStyleNode = document.createElement('style');
	this.responsiveStyleNode = document.createElement('style');
	this.isShortDateMode = false;

	// responsive column data must be passed in settings
	this.columns = [];
	this.columnStyles = '';
	this.numRows = 0;
	this.paddingCollapse = typeof this.s.paddingCollapse !== 'undefined' ? this.s.paddingCollapse : 8;

	this.firstRow = null;

	this.isDetailedMode = this.$outer.hasClass('is-detailed-mode');
	this.currentFilter = '';

	this.members = [];
	this.dataSets = {};

	var _multiselects = [];

	if (this.s.members) {
		this.members = this.s.members.replace(/ /g, '').split(',');
	} else {
		this.members.push(this.id);
	}

	if (!this.s.columns) {
		throw new Error('No column data passed to settings object.');
	}

	// concat to kill reference to column settings array and sort by fixed/displaypriority
	this.columns = this.s.columns.concat().sort(function(a, b) {
		// required columns first, then ordered by hide order
		var aVal = a.hideOrder || 0;
		var bVal = b.hideOrder || 0;

		return window.sortNumeric(aVal, bVal, 1);
	});

	// Optional settings
	this.useShortDates = this.s.shortDateWidth > 0;

	// Sort properties
	this.initialSort = {};
	this.currentSort = {};

	if (this.s.isSortable) {
		// save reference to initial sort config to compare against on unload.
		this.initialSort.method = this.s.sortMethod !== 'undefined' ? this.s.sortMethod : 'source';
		this.initialSort.order = this.s.sortOrder !== 'undefined' ? this.s.sortOrder : 1;
		this.initialSort.type = this.s.sortType !== 'undefined' ? this.s.sortType : 'numeric';

		this.currentSort.method = this.s.sortMethod !== 'undefined' ? this.s.sortMethod : 'source';
		this.currentSort.order = this.s.sortOrder !== 'undefined' ? this.s.sortOrder : 1;
		this.currentSort.type = this.s.sortType !== 'undefined' ? this.s.sortType : 'numeric';
		this.secondarySort = this.s.secondarySort;
	}

	this.loadColumnStyles = function() {
		// Add the <style> element to the document head
		this.baseStyleNode.id = this.id + '-baseStyles';
		this.responsiveStyleNode.id = this.id + '-responsiveStyles';
		this.baseStyleNode.appendChild(document.createTextNode('')); // Webkit needs this for some reason
		this.responsiveStyleNode.appendChild(document.createTextNode('')); // Webkit needs this for some reason

		document.head.appendChild(this.baseStyleNode);
		document.head.appendChild(this.responsiveStyleNode);

		this.columns.forEach(function(col) {
			col.selector = '#' + this.id + ' .js-col-' + col.header;

			var str = '';

			if (!col.native && !col.minWidth) {
				throw new Error('Column ' + col.header + ' must either be native or have a mininum width');
			}

			if (col.hideOrder > 0 && !col.minWidth) {
				throw new Error('minWidth not set for hideable column, "' + col.header + '"');
			}

			if (col.minWidthSmall && !col.minWidth) {
				throw new Error('Do not use minWidthSmall without also specifying minWidth.');
			}

			// build styles string based on column options
			if (col.minWidthSmall) {
				str += '  width:' + col.minWidthSmall + 'px;\n';
			} else if (col.minWidth) {
				str += '  width:' + col.minWidth + 'px;\n';
			}
			if (col.grow) {
				str += '  flex-grow:' + col.grow + ';\n';
			}
			if (col.align) {
				str += '  text-align: ' + col.align + ';\n';
			}
			if (col.overflow) {
				str += '  text-overflow: ' + col.overflow + ';\n';
			}
			if (col.maxWidth) {
				str += '  max-width: ' + col.maxWidth + 'px;\n';
			}

			if (str.length) {
				this.baseStyleNode.innerHTML += col.selector + '{\n' + str + '\n}';
			}

			// if there was a minWidth, add the media query for large screens here so it is in the proper
			// order to leverage the cascade
			if (col.minWidthSmall) {
				str = '@media screen and (min-width:768px) {\n';
				str += ' #' + this.id + ' .js-col-' + col.header + '{\n';
				str += '   width:' + col.minWidth + 'px;\n';
				str += ' }\n}\n\n';

				this.baseStyleNode.innerHTML += str;
			}

		}, this);
	};

	this.getTotalWidth = function() {
		// if the table rows are visible, just read the width from the first one
		if ($(this.firstRow).is(':visible')) {
			return $(this.firstRow).width();
		}
		// if table rows are hidden, infer the width from $outer
		var r = window.getComputedStyle(this.firstRow);
		// calculate total width from $outer because rows are currently hidden
		var rowPaddingLeft = parseInt(r.paddingLeft) || 0;
		var rowPaddingRight = parseInt(r.paddingRight) || 0;
		return Math.min(this.$outer.width() - rowPaddingLeft - rowPaddingRight);
	};

	this.getSortedRows = function($rows, headerName, sortOrder, sortType, secondarySort) {
		var aVal;
		var bVal;
		var aVal2;
		var bVal2;

		this.currentSort.method = headerName;
		this.currentSort.order = sortOrder;
		this.currentSort.type = sortType;

		if (sortType === 'numeric') {
			return $rows.sort(function(a, b) {
				var aCell = a.querySelector('.js-col-' + headerName);
				var bCell = b.querySelector('.js-col-' + headerName);

				aVal = parseFloat(aCell.getAttribute('data-sort-value'), 10);
				bVal = parseFloat(bCell.getAttribute('data-sort-value'), 10);
				if (secondarySort) {
					aVal2 = a.querySelector('.js-col-' + secondarySort).getAttribute('data-sort-value');
					bVal2 = b.querySelector('.js-col-' + secondarySort).getAttribute('data-sort-value');

					return window.sortNumericWithSecondary(aVal, bVal, sortOrder, aVal2, bVal2, 'numeric');
				}

				return window.sortNumeric(aVal, bVal, sortOrder);
			});
		}

		if (sortType === 'alpha') {
			return $rows.sort(function(a, b) {
				var aCell = a.querySelector('.js-col-' + headerName);
				var bCell = b.querySelector('.js-col-' + headerName);
				try {
					aVal = aCell.getAttribute('data-sort-value') ? aCell.getAttribute('data-sort-value').toLowerCase() : aCell.textContent.trim().toLowerCase();
					bVal = bCell.getAttribute('data-sort-value') ? bCell.getAttribute('data-sort-value').toLowerCase() : bCell.textContent.trim().toLowerCase();

				} catch (err) {
					aVal = '';
					bVal = '';
				}

				if (secondarySort) {
					aVal2 = aCell.parentNode.querySelector('.js-col-' + secondarySort).getAttribute('data-sort-value');
					bVal2 = bCell.parentNode.querySelector('.js-col-' + secondarySort).getAttribute('data-sort-value');

					return window.sortAlphaWithSecondary(aVal, bVal, sortOrder, aVal2, bVal2, 'alpha');
				}

				return window.sortAlpha(aVal, bVal, sortOrder);
			});
		}
	}; // end create sort map


	this.sortByColumn = function(dataSet, sortMethod, sortOrder, sortType) {
		// fail if no cells to sort by.
		if (!dataSet.$body.find('.js-col-' + sortMethod).length) return;

		// we are always secondary sorting by date, so ok to hardcode that in, until when/if we change that
		dataSet.$rows = this.getSortedRows(dataSet.$rows, sortMethod, sortOrder, sortType, this.secondarySort);
		dataSet.sortPending = true;

		this.currentSort.order = sortOrder;

		this.doSaveSortPrefPost();
	};

	this.unloadSortedColumns = function(isInit) {
		var s;
		for (var set in this.dataSets) {
			s = this.dataSets[set];

			if (s.sortPending) {
				s.$body.html(s.$rows);
				s.sortPending = false;
			}
		}

		if (typeof this.afterSort === 'function') {
			this.afterSort(isInit);
		}
	};

	// find the native width of an html node.
	var _getNativeWidth = function(cell) {
		var w = 0;
		var computedStyle = window.getComputedStyle(cell);
		this.$widthTester.html(cell.innerHTML);
		if (computedStyle.fontWeight == 600) {
			this.$widthTester.css('font-weight', '600');
		}
		w = this.$widthTester.width();
		if (!this.$widthTester.html)
			this.$widthTester
				.css('')
				.html('');

		return w + parseInt(computedStyle.paddingLeft) + parseInt(computedStyle.paddingRight);
	}.bind(this);

	this.showShortDates = function() {
		this.$outer.find('[data-date-short]').each(function() {
			var shortDate = this.getAttribute('data-date-short') || '';
			this.textContent = shortDate;
		});
	};

	this.showExpandedDates = function() {
		this.$outer.find('[data-date-short]').each(function() {
			var longDate = this.getAttribute('data-date-long') || '';
			this.textContent = longDate;
		});
	};

	this.doDateDisplayLogic = function(totalWidth) {
		if (!this.isShortDateMode) {
			// don't show compact dates if there is room to show expanded dates.
			if (totalWidth > this.s.shortDateWidth) return;

			this.showShortDates();
			this.isShortDateMode = true;
		} else {
			// don't show expanded dates if there is not room for them.
			if (totalWidth < this.s.shortDateWidth) return;

			this.showExpandedDates();
			this.isShortDateMode = false;
		}
	};

	this.sizeColumns = function() {
		// abort here if table does not have any rows
		if (this.numRows < 1) return;

		var totalWidth = this.getTotalWidth();

		if (this.useShortDates) {
			this.doDateDisplayLogic(totalWidth);
		}

		if (typeof this.beforeSizeColumns === 'function') {
			this.beforeSizeColumns();
		}

		this.distributeAvailableWidth(totalWidth);
	};

	this.distributeAvailableWidth = function(availableWidth) {
		var col;
		var i = 0;
		var len = 0;
		var minWidth = 0;
		var windowWidth = $(window).width();
		// loop through all columns and display as many as possible
		for (i = 0, len = this.columns.length; i < len; i++) {
			col = this.columns[i];

			// minimum width is read from clientWidth property for native columns
			if (col.minWidthSmall && windowWidth < 768) {
				minWidth = col.minWidthSmall;
			} else {
				minWidth = !col.native ? col.minWidth : _getNativeWidth(col.key);
			}

			// show all required columns first
			if (!col.hideOrder) {
				if (availableWidth < minWidth) {
					logger.warn('not enough room to show required column ' + col.header + ' at minimum width');
					this.showColumn(col, availableWidth);
					availableWidth = 0;
					continue;
				}

				if (col.native) {
					this.showColumn(col, minWidth);
				} else {
					this.showColumn(col);
				}
				availableWidth -= minWidth;
				continue;
			}

			// Now figure out which hideable columns we have room for
			if (availableWidth > minWidth) {
				this.showColumn(col);
				availableWidth -= minWidth;
			} else {
				this.hideColumn(col);
			}
		}

		// apply columnstyles queued via this.showColumn and this.hideColumn methods
		this.responsiveStyleNode.innerHTML = this.columnStyles;
		this.columnStyles = '';

		if (typeof this.afterDistributeAvailableWidth === 'function') {
			this.afterDistributeAvailableWidth();
		}
	};

	this.showColumn = function(col, width) {
		if (width) this.columnStyles += '#' + this.id + ' .js-col-' + col.header + ' {width: ' + width + 'px;}';
		if (!col.hideOrder && this.s.collapse === 'wrap') {
			this.$outer.find('.js-expandable-' + col.header)
				.addClass('is-hidden');
		}

		if (typeof this.afterShowColumn === 'function') {
			this.afterShowColumn(col);
		}
	};

	this.hideColumn = function(col) {
		this.columnStyles += '#' + this.id + ' .js-col-' + col.header + ' {display:none;}';

		if (!col.hideOrder && this.s.collapse === 'wrap') {
			this.$outer.find('.js-expandable-' + col.header)
				.removeClass('is-hidden');
		}

		if (typeof this.afterHideColumn === 'function') {
			this.afterHideColumn(col);
		}
	};

	this.saveSortPrefTimer = null;

	this.doSaveSortPrefPost = window.debounce(function() {
		var str = 'WalletService.aspx?';
		var doPost = false;

		for (var prop in this.initialSort) {
			if (this.initialSort.hasOwnProperty(prop)) {
				if (this.initialSort[prop] !== this.currentSort[prop]) {
					str += 'walletSort' + prop.toLowerCase().capitalizeFirstLetter() + '=' + this.currentSort[prop] + '&';
					// update initial sort to reflect the new info we stored in the db
					this.initialSort[prop] = this.currentSort[prop];
				}
				doPost = true;
			}
		}

		if (doPost) {
			$.get(str);
		}

	}, 300);

	this.normalizeSelectNames = function(newName) {
		this.$outer.find('.js-rt2-select').attr('name', newName);
	};

	this.handleResize = function() {
		this.sizeColumns(false);
	};

	this.toggleRowExpand = function($row) {
		// Don't toggle if there was more than 1 click in rapid succession
		if ($row.hasClass('is-expanded')) {
			$row.removeClass('is-expanded');
			$row.find('.js-expand-icon').attr('src', 'images/icons-v2/carat-right-no-frame-20px.svg');
		} else {
			$row.addClass('is-expanded');
			$row.find('.js-expand-icon').attr('src', 'images/carat-down-no-frame-20px.svg');
		}

		// Sometimes toggling a row causes a scrollbar to appear on the window which throws off
		// cell width calculations so reset them once expanded style has been applied
		this.sizeColumns(true);
	};

	this.toggleDetailedMode = function($toggle) {
		this.$outer.toggleClass('is-detailed-mode');
		this.isDetailedMode = this.$outer.hasClass('is-detailed-mode');

		$toggle.toggleClass('is-off');

		// update translated tooltip
		if (this.isDetailedMode) {
			$toggle.attr('title', $toggle.data().tooltipOn);
		} else {
			$toggle.attr('title', $toggle.data().tooltipOff);
		}
	};

	// ------------------------------------
	// Filter Methods
	// ------------------------------------
	this.getFilterValue = function() {
		var val = '';
		var postParamName = '';

		_multiselects.forEach(function(obj) {
			if (!obj.getValue()) return;

			postParamName = obj.postParamName || obj.id;

			if (!val.length) {
				val += postParamName + '=';
			} else {
				val += '&' + postParamName + '=';
			}

			val += obj.getValue();
		});

		return val;
	};

	var _doFilterPost = function() {
		if (this.getFilterValue() === this.currentFilter) {
			logger.log('Filter has not changed');
			return;
		}

		window.__doPostBack(this.s.updatePanelID, this.getFilterValue());

		// update current filter after successful postback.
		this.currentFilter = this.getFilterValue();
	};

	this.doFilterPostDebounced = window.debounce(_doFilterPost, 500);

	// ------------------------------------
	// Initialization Methods
	// ------------------------------------
	this.cacheSelectors = function() {
		this.$outer = $('#' + this.id);
		this.$rows = this.$outer.find('.js-rt2-row');
		this.$filters = this.$outer.find('.js-rt2-filter');
		this.numRows = this.$rows.length;

		this.firstRow = this.$rows.first()[0];

		this.members.forEach(function(id) {
			// make sure that dataset member exists
			if (!$('#' + id).length) {
				logger.warn('No element found for Member id: ' + id + ' for Feed ' + this.id);
				return;
			}

			this.dataSets[id] = {};

			let currentSet = {};
			currentSet.$outer = $('#' + id);

			if (!currentSet.$outer.length) throw new Error('No element found for Member id: ' + id + ' for Feed ' + this.id);

			currentSet.$body = currentSet.$outer.find('.js-rt2-body') || currentSet.$outer;
			currentSet.$rows = currentSet.$body.find('.js-rt2-row');
			this.dataSets[id] = currentSet;
		}, this);

		this.columns.forEach(function(col) {
			if (col.native) {
				var cells = this.$outer[0].querySelectorAll('.js-col-' + col.header);

				var w = 0;
				var i = 0;
				var currentWidth = 0;
				// create a hidden div which we will drop content into to determine native width of cell contents
				if (!this.$widthTester) {
					this.$widthTester = this.$widthTester || $('<div style="display:inline-block;position:absolute;left:-9999px; visibility:hidden;"></div>');
					this.$outer.append(this.$widthTester);
				}

				while (i < cells.length) {
					currentWidth = _getNativeWidth(cells[i]);
					// if current cell is the widest we've found yet, set it as the column key
					if (currentWidth > w) {
						col.key = cells[i];
						w = currentWidth;
					}
					i++;
				}
			}
		}, this);

		if (typeof this.cacheAdditionalSelectors === 'function') this.cacheAdditionalSelectors();
	};

	this.attachEvents = function() {
		var that = this;
		// ----------------------------------
		// Attach selectable table events
		// ----------------------------------
		if (this.s.isSortable) {
			// sort all datasets
			for (let set in this.dataSets) {
				this.sortByColumn(this.dataSets[set], this.initialSort.method, this.initialSort.order, this.initialSort.type, true);
			}
			for (let set in this.dataSets) {
				this.unloadSortedColumns(this.dataSets[set]);
			}

			this.$outer.on('click.rt', '.js-sort', function() {
				var d = $(this).data();
				var sortColumn = d.header ? d.header : $(this).parent().data().header;
				var sortOrder = d.sortDefault || 1;
				var sortType = d.sortType || 'alpha';
				var dataSet;

				if (that.s.members) {
					dataSet = that.dataSets[$(this).parents('.js-rt2-data').attr('id')];
				} else {
					dataSet = that.dataSets[that.id];
				}

				if (that.currentSort.method === sortColumn) {
					sortOrder = that.currentSort.order * -1;
				}
				that.sortByColumn(dataSet, sortColumn, sortOrder, sortType);
				that.unloadSortedColumns(true);
			});
		}

		// ----------------------------------
		// Attach selectable table events
		// ----------------------------------
		// If the responsive table has selectable items, attach events to control checkbox/radio interaction
		if (this.$outer.find('.js-rt2-select-all').length) {
			this.$outer.on('change', '.js-rt2-select-all', function() {
				var value = this.checked;
				var $tableData = $(this).parents('.js-rt2-data');

				$tableData.find('.js-rt2-select')
					.each(function() {
						if (!$(this).attr('disabled')) {
							$(this).attr('checked', value);
							$(this).prop('checked', value);
						}
						if ($(this).is('span')) {
							var nestedCheckbox = $(this).find('[type=checkbox]');
							if (!nestedCheckbox.attr('disabled')) {
								nestedCheckbox.attr('checked', value);
								nestedCheckbox.prop('checked', value);
							}
						}
					});
			});

		}
		if (this.$outer.find('.js-rt2-select').length) {
			this.$outer.on('change', '.js-rt2-select', function() {
				if (this.type === 'radio') {
					that.$outer.find('.js-rt2-select').not($(this)).each(function() {
						this.removeAttribute('checked');
					});
				}

				if (this.checked) {
					this.setAttribute('checked', 'checked');
				} else {
					this.removeAttribute('checked');
				}
			});

		}

		// ---------------------------------------
		// Attach filter events
		// ---------------------------------------
		if (this.$filters.length) {

			if (_multiselects.length)  {
				_multiselects.forEach(function(objMultiSelect) {
					objMultiSelect.onValueChanged = function() {
						that.doFilterPostDebounced();
					};
				});
			}
		}

		// ---------------------------------------
		// Attach collapsible table events
		// ---------------------------------------
		if (this.$outer.find($('.js-expand')).length) {
			var _toggleRowExpandTimer = null;
			var _rowMouseXPos = 0;
			var _rowMouseYPos = 0;
			var _rowMouseDownTime = 0;
			var _numRowClicks = 0;
			var _cancelRowExpand = false;
			var _delayRowExpand = false;

			this.$outer.on('mousedown.rt', '.js-expand', function(e) {
				if (this.isDetailedMode) return;
				var $target = $(e.target);
				var nodeName = e.target.nodeName;

				_rowMouseDownTime = Date.now();
				_rowMouseXPos = e.screenX;
				_rowMouseYPos = e.screenY;

				if ($target.hasClass('js-modal-open') || nodeName === 'INPUT' || $target.hasClass('js-edit') || $target.parent().hasClass('js-edit') || $target.hasClass('feed_edit-item')) {
					_cancelRowExpand = true;
					return;
				}

				// We don't want to toggle the row on double click if we are hovering over text
				if (nodeName === 'SPAN') {
					_delayRowExpand = true;
					return;
				}
			});

			// handle clicks on expandable rows
			this.$outer.on('click.rt', '.js-expand', function(e) {
				if (that.isDetailedMode) return;
				var $row = $(this);

				var diffX = Math.abs(_rowMouseXPos - e.screenX);
				var diffY = Math.abs(_rowMouseYPos - e.screenY);
				var time = Date.now();

				// Cancel click if we clicked on a target that should not cause an expand
				if (_cancelRowExpand) {
					_cancelRowExpand = false;
					return;
				}

				// Cancel click event if mouse was held down longer than 500ms
				if (time - _rowMouseDownTime > 500) return;

				// Cancel the click event if mouse moved more than 100px
				if ((diffX + diffY) > 100) return;

				if (_numRowClicks < 1 && _delayRowExpand) {
					// clear a running timer if there is one
					clearTimeout(_toggleRowExpandTimer);

					// start a new cancellable toggle event
					_toggleRowExpandTimer = setTimeout(function() {
						that.toggleRowExpand($row);
						// reset row click stuff
						_delayRowExpand = false;
						_numRowClicks = 0;
					}, 200);

					_numRowClicks++;
					return;
				}

				if (_numRowClicks < 1) {
					that.toggleRowExpand($row);
					// reset row click stuff
					clearTimeout(_toggleRowExpandTimer);
					_delayRowExpand = false;
					_numRowClicks = 0;
				} else {
					clearTimeout(_toggleRowExpandTimer);
					_delayRowExpand = false;
					_numRowClicks = 0;
				}
			});
		}

		// ---------------------------------------
		// Detailed mode events
		// ---------------------------------------
		this.$outer.on('click', '.js-toggle-detailed', function() {
			that.toggleDetailedMode($(this));
		});
	};
	this.init = function() {
		var that = this;

		// don't do init if outer elem doesn't exist
		if (!$('#' + id).length) return;

		if (!this.columnStylesLoaded) {
			logger.warn('Column styles should be loaded in the DOM prior to the table being rendered to prevent FOUC');
			this.loadColumnStyles();
		}

		// prevent reattachment of responsive table events.
		this.cacheSelectors();

		// abort here if no rows as the empty state will be shown instead
		if (this.numRows < 1) {
			window.layoutQueue.remove('handleResize', this);
			return;
		}

		if (typeof this.beforeInit === 'function') {
			this.beforeInit(this);
		}
		// If using a normalized select name, apply it here.
		if (this.s.selectName) {
			this.normalizeSelectNames(this.s.selectName);
		}

		// do initial column sizing
		this.sizeColumns(true);


		if (typeof this.afterInit === 'function') {
			this.afterInit(this);
		}

		window.layoutQueue.add('handleResize', this);

		$(function() {
			if (that.$filters.length) {
				if (!_multiselects.length) {
					that.$filters.each(function() {
						_multiselects.push(window.multiselects[$(this).attr('id')]);
					});
				}
				that.currentFilter = that.getFilterValue();
			}
			that.attachEvents();
		});
	};
}